import isNil from 'lodash/isNil';
import { addDays, addMonths, differenceInCalendarDays, format, isValid, parseISO } from 'date-fns';
import { CONTROL_TYPES } from '@/components/AppInput.vue';

// date-fns tokens, deliberately not @/utils/date's moment-token DEFAULT_DATE_FORMAT.
const ISO_DATE = 'yyyy-MM-dd';

// User-selectable filter operators. Wire convention (shared with paydays-api): ?field[op]=value; bare ?field=value === eq.
export const OPERATORS = {
  EQ: 'eq',
  NE: 'ne',
  IN: 'in',
  NIN: 'nin',
  GT: 'gt',
  GTE: 'gte',
  LT: 'lt',
  LTE: 'lte',
  CONTAINS: 'contains',
  BETWEEN: 'between',
};

export const OPERATOR_LABELS = {
  [OPERATORS.EQ]: 'Equals',
  [OPERATORS.NE]: 'Not equal',
  [OPERATORS.IN]: 'Any of',
  [OPERATORS.NIN]: 'None of',
  [OPERATORS.GT]: 'Greater than',
  [OPERATORS.GTE]: 'Greater than or equal',
  [OPERATORS.LT]: 'Less than',
  [OPERATORS.LTE]: 'Less than or equal',
  [OPERATORS.CONTAINS]: 'Contains',
  [OPERATORS.BETWEEN]: 'Between',
};

// Compact symbols for the applied-filter chips, so users can see exactly which operator is active.
export const OPERATOR_SYMBOLS = {
  [OPERATORS.EQ]: '=',
  [OPERATORS.NE]: '≠',
  [OPERATORS.IN]: '=',
  [OPERATORS.NIN]: '≠',
  [OPERATORS.GT]: '>',
  [OPERATORS.GTE]: '≥',
  [OPERATORS.LT]: '<',
  [OPERATORS.LTE]: '≤',
  [OPERATORS.CONTAINS]: 'contains',
  [OPERATORS.BETWEEN]: 'between',
};

// Semantic filter types drive the default operator menu (distinct from AppInput's widget CONTROL_TYPES).
export const FILTER_TYPES = {
  CATEGORICAL: 'categorical',
  DATE: 'date',
  NUMBER: 'number',
  TEXT: 'text',
};

const OPERATORS_BY_TYPE = {
  [FILTER_TYPES.CATEGORICAL]: [OPERATORS.EQ, OPERATORS.NE, OPERATORS.IN],
  [FILTER_TYPES.DATE]: [OPERATORS.EQ, OPERATORS.NE, OPERATORS.GT, OPERATORS.GTE, OPERATORS.LT, OPERATORS.LTE],
  [FILTER_TYPES.NUMBER]: [OPERATORS.EQ, OPERATORS.NE, OPERATORS.GT, OPERATORS.GTE, OPERATORS.LT, OPERATORS.LTE],
  [FILTER_TYPES.TEXT]: [OPERATORS.EQ, OPERATORS.NE, OPERATORS.CONTAINS],
};

// AppInput widget type -> semantic filter type, so existing column descriptors get sane defaults.
const CONTROL_TYPE_TO_FILTER_TYPE = {
  [CONTROL_TYPES.CHECKBOX]: FILTER_TYPES.CATEGORICAL,
  [CONTROL_TYPES.CHECKBOX_SINGLE]: FILTER_TYPES.CATEGORICAL,
  [CONTROL_TYPES.RADIO]: FILTER_TYPES.CATEGORICAL,
  [CONTROL_TYPES.SELECT]: FILTER_TYPES.CATEGORICAL,
  [CONTROL_TYPES.DATE]: FILTER_TYPES.DATE,
  [CONTROL_TYPES.TIME]: FILTER_TYPES.NUMBER,
  [CONTROL_TYPES.NUMBER]: FILTER_TYPES.NUMBER,
  [CONTROL_TYPES.TEXT]: FILTER_TYPES.TEXT,
  [CONTROL_TYPES.INPUT]: FILTER_TYPES.TEXT,
  [CONTROL_TYPES.EMAIL]: FILTER_TYPES.TEXT,
  [CONTROL_TYPES.TEL]: FILTER_TYPES.TEXT,
  [CONTROL_TYPES.TEXTAREA]: FILTER_TYPES.TEXT,
  [CONTROL_TYPES.PASSWORD]: FILTER_TYPES.TEXT,
};

export const filterTypeForControl = controlType => CONTROL_TYPE_TO_FILTER_TYPE[controlType] || FILTER_TYPES.TEXT;

export const operatorsForType = filterType => OPERATORS_BY_TYPE[filterType] || [OPERATORS.EQ];

// Operators a column offers: explicit `operators` wins, else the semantic-type default, else eq-only.
export const availableOperators = filter =>
  filter?.operators?.length ? filter.operators : operatorsForType(filterTypeForControl(filter?.type));

// Default/fixed operator for a column (first offered, or explicit defaultOperator, else eq).
export const operatorFor = filter => filter?.defaultOperator || availableOperators(filter)[0] || OPERATORS.EQ;

const IN_OPERATORS = [OPERATORS.IN, OPERATORS.NIN];
export const isMultiValueOperator = op => IN_OPERATORS.includes(op);

// Ready-made operator lists for column descriptors, so callers don't re-spell them per column.
export const COMPARISON_OPERATORS = [
  OPERATORS.GTE,
  OPERATORS.LTE,
  OPERATORS.BETWEEN,
  OPERATORS.EQ,
  OPERATORS.NE,
  OPERATORS.GT,
  OPERATORS.LT,
];
export const TEXT_MATCH_OPERATORS = [OPERATORS.CONTAINS, OPERATORS.EQ, OPERATORS.NE];

// Accept both the flat scalar model ({key: value}) and the structured one ({key: {op, value}}).
const normalizeEntry = entry =>
  entry && typeof entry === 'object' && !Array.isArray(entry) && 'value' in entry
    ? { op: entry.op || OPERATORS.EQ, value: entry.value }
    : { op: OPERATORS.EQ, value: entry };

// Shared "don't send this" test. Also treats a range object with no bounds as empty.
export const isEmptyValue = value => {
  if (isNil(value) || value === '') {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === 'number') {
    return Number.isNaN(value);
  }
  if (typeof value === 'string') {
    // Anchored: a text value merely containing "false" (e.g. false@acme.com) is a real filter.
    return value.trim().toLowerCase() === 'false';
  }
  if (typeof value === 'object') {
    return isEmptyValue(value.from) && isEmptyValue(value.to);
  }
  return false;
};

const OP_KEY_RE = /^(.+)\[(\w+)\]$/;

// {key: {op, value}} (or flat {key: value}) -> flat query/param object; eq emits bare `key`, non-eq emits `key[op]`.
export const serializeFilters = (filters = {}) =>
  Object.entries(filters).reduce((acc, [key, entry]) => {
    const { op, value } = normalizeEntry(entry);
    // `between` has no native backend operator — split it into gte + lte, which the translator already understands.
    if (op === OPERATORS.BETWEEN) {
      const { from, to } = value || {};
      if (!isEmptyValue(from)) {
        acc[`${key}[${OPERATORS.GTE}]`] = from;
      }
      if (!isEmptyValue(to)) {
        acc[`${key}[${OPERATORS.LTE}]`] = to;
      }
      return acc;
    }
    if (isEmptyValue(value)) {
      return acc;
    }
    acc[op === OPERATORS.EQ ? key : `${key}[${op}]`] = value;
    return acc;
  }, {});

// Scale numeric filter values by a per-field factor (e.g. money major units -> integer minor units the backend stores).
export const scaleFilterValues = (structured = {}, factorByField = {}) =>
  Object.entries(structured).reduce((acc, [key, entry]) => {
    const factor = factorByField[key];
    if (!factor) {
      acc[key] = entry;
      return acc;
    }
    const { op, value } = normalizeEntry(entry);
    const scale = v => (isEmptyValue(v) ? v : Math.round(Number(v) * factor));
    if (op === OPERATORS.BETWEEN) {
      const { from, to } = value || {};
      acc[key] = { op, value: { from: scale(from), to: scale(to) } };
    } else if (Array.isArray(value)) {
      acc[key] = { op, value: value.map(scale) };
    } else {
      acc[key] = { op, value: scale(value) };
    }
    return acc;
  }, {});

// flat query object -> {key: {op, value}}; bare `key` hydrates as eq. A key carrying both gte+lte recombines to `between`.
export const parseFiltersFromQuery = (query = {}) => {
  const byKey = {};
  Object.entries(query).forEach(([rawKey, value]) => {
    const match = rawKey.match(OP_KEY_RE);
    const key = match ? match[1] : rawKey;
    const op = match ? match[2] : OPERATORS.EQ;
    byKey[key] = byKey[key] || [];
    byKey[key].push({ op, value });
  });
  const validOps = Object.values(OPERATORS);
  return Object.entries(byKey).reduce((acc, [key, all]) => {
    // Drop unknown operators from a hand-edited/stale URL — sending them back would 400.
    const conds = all.filter(c => validOps.includes(c.op));
    if (!conds.length) {
      return acc;
    }
    const gte = conds.find(c => c.op === OPERATORS.GTE);
    const lte = conds.find(c => c.op === OPERATORS.LTE);
    if (conds.length === 2 && gte && lte) {
      acc[key] = { op: OPERATORS.BETWEEN, value: { from: gte.value, to: lte.value } };
    } else {
      const { op, value } = conds[0];
      // A single ?source[in]=A parses as a string; multi-value operators must stay arrays.
      acc[key] = { op, value: isMultiValueOperator(op) && !Array.isArray(value) ? [value] : value };
    }
    return acc;
  }, {});
};

// flat {key: value} -> structured {key: {op, value}} (per-key op via `defaults`, else eq).
export const toStructured = (flat = {}, defaults = {}) =>
  Object.entries(flat).reduce((acc, [key, value]) => {
    acc[key] = { op: defaults[key] || OPERATORS.EQ, value };
    return acc;
  }, {});

// Anchored, so a partial date mid-typing (e.g. 2026-06-0) never counts as complete.
export const FULL_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Shape AND reality: '2026-13-45' matches the pattern but is not a date, and date-fns throws on formatting it.
export const isIsoDate = value => FULL_DATE_RE.test(value || '') && isValid(parseISO(value));

// Reject a typed value the backend would 400 on (numbers must be finite, dates must be complete).
export const isValidTypedValue = (filterType, value) => {
  if (filterType === FILTER_TYPES.NUMBER) {
    return Number.isFinite(Number(value));
  }
  if (filterType === FILTER_TYPES.DATE) {
    return isIsoDate(String(value));
  }
  return true;
};

// What to tell the user when their typed value is rejected.
export const VALUE_FORMAT_HINTS = {
  [FILTER_TYPES.NUMBER]: 'Enter a number, e.g. 250',
  [FILTER_TYPES.DATE]: 'Use the format YYYY-MM-DD',
};

// --- Filter-bar decision logic. Pure so it can be tested; the component keeps only the reactive glue. ---

// Columns still offerable: not hidden, not already applied, not the one being edited, and label-matching what's typed.
export const suggestFilterColumns = (filters = [], { query = '', committedKeys = new Set(), draftKey = null } = {}) => {
  const q = query.trim().toLowerCase();
  return filters
    .filter(filter => filter.hidden !== true && !committedKeys.has(filter.key) && filter.key !== draftKey)
    .filter(filter => !q || filter.label.toLowerCase().includes(q));
};

// Operator typeahead, matched on the human label ("eq" -> Equals / Not equal).
export const matchOperatorsByLabel = (operators = [], query = '') => {
  const q = query.trim().toLowerCase();
  return q ? operators.filter(op => (OPERATOR_LABELS[op] || op).toLowerCase().includes(q)) : operators;
};

// Option typeahead, matched on the visible text so single-choice columns behave like the column/operator stages.
export const matchOptionsByLabel = (options = [], query = '') => {
  const q = query.trim().toLowerCase();
  return q ? options.filter(option => String(option.text).toLowerCase().includes(q)) : options;
};

// Arrow-key movement: nothing highlighted enters from the matching end, otherwise wrap.
export const nextHighlightIndex = (current, direction, length) => {
  if (!length) {
    return -1;
  }
  if (current < 0) {
    return direction > 0 ? 0 : length - 1;
  }
  return (current + direction + length) % length;
};

// `between` and option lists are edited inside the menu; everything else is typed in the bar itself.
export const operatorUsesMenu = (op, filter) => op === OPERATORS.BETWEEN || !!filter?.options?.length;

// A fresh draft value has to match the operator's shape, or the wrong control renders.
export const initialValueForOperator = op => {
  if (isMultiValueOperator(op)) {
    return [];
  }
  return op === OPERATORS.BETWEEN ? {} : '';
};

// `between` needs a {from,to} and validates each bound separately — one-sided ranges are legal, both are free text.
export const isCommittableFilterValue = (filterType, op, value) => {
  if (op !== OPERATORS.BETWEEN) {
    return isValidTypedValue(filterType, value);
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  return [value.from, value.to].every(bound => isEmptyValue(bound) || isValidTypedValue(filterType, bound));
};

// --- Bounded date ranges. A column opts in with `maxRangeDays` / `noFuture`; uncapped columns are unaffected. ---

export const todayIso = () => format(new Date(), ISO_DATE);

const shiftIso = (date, days) => format(addDays(parseISO(date), days), ISO_DATE);

// Keeps the day-of-month, clamping to the month end (31 Jan +1 month = 28 Feb) rather than spilling into the next.
export const shiftMonthsIso = (date, months) => format(addMonths(parseISO(date), months), ISO_DATE);

// Typing bypasses the calendar bounds, so the same rules are re-checked on commit. Returns '' when acceptable.
// Both bounds are required once capped: a one-sided range has no width the cap can be measured against.
export const dateRangeError = (filter, value, today = todayIso()) => {
  const max = filter?.maxRangeDays;
  if (!max) {
    return '';
  }
  const { from, to } = value || {};
  if (isEmptyValue(from) || isEmptyValue(to)) {
    return 'Pick both a from and a to date';
  }
  if (from > to) {
    return 'The from date must be on or before the to date';
  }
  if (dateRangeSpanDays(from, to) > max) {
    return `Pick a range of ${max} days or fewer`;
  }
  if (filter?.noFuture && to > today) {
    return 'The to date cannot be in the future';
  }
  return '';
};

// Days between the bounds — the single measure maxRangeDays is checked against, so nothing can disagree with it.
export const dateRangeSpanDays = (from, to) =>
  from && to ? differenceInCalendarDays(parseISO(to), parseISO(from)) : null;

// Picking one bound too far from the other drags the other alongside it, rather than greying the calendar or erroring.
// Both bounds must be complete: snapping mid-typing would move the far bound off a half-typed date.
export const reconcileDateRange = ({ from, to } = {}, edited, { maxRangeDays, snapRangeMonths, noFuture } = {}) => {
  if (!maxRangeDays || !isIsoDate(from) || !isIsoDate(to)) {
    return { from, to };
  }
  const today = todayIso();
  const clamp = date => (noFuture && date > today ? today : date);
  const next = { from: clamp(from), to: clamp(to) };
  if (next.from <= next.to && dateRangeSpanDays(next.from, next.to) <= maxRangeDays) {
    return next;
  }
  // Snap in whole months so the pair keeps the same day-of-month; N months is what maxRangeDays was sized to allow.
  const snap = (date, direction) =>
    snapRangeMonths ? shiftMonthsIso(date, direction * snapRangeMonths) : shiftIso(date, direction * maxRangeDays);
  return edited === 'to'
    ? { from: snap(next.to, -1), to: next.to }
    : { from: next.from, to: clamp(snap(next.from, 1)) };
};

// A one-sided `between` reads as >= or <=, which is what it actually sends. isEmptyValue, not truthiness: 0 is a bound.
export const operatorSymbolFor = (op, value) => {
  if (op === OPERATORS.BETWEEN && value && typeof value === 'object') {
    const hasFrom = !isEmptyValue(value.from);
    const hasTo = !isEmptyValue(value.to);
    if (hasFrom && !hasTo) {
      return OPERATOR_SYMBOLS[OPERATORS.GTE];
    }
    if (!hasFrom && hasTo) {
      return OPERATOR_SYMBOLS[OPERATORS.LTE];
    }
  }
  return OPERATOR_SYMBOLS[op] || ':';
};

const optionText = (filter, optionValue) =>
  filter?.options ? filter.options.find(option => option.value === optionValue)?.text || optionValue : optionValue;

// Chip text. A range keeps a 0 bound, so it's filtered by isEmptyValue rather than truthiness.
export const formatFilterValue = (filter, value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const bounds = [value.from, value.to].filter(bound => !isEmptyValue(bound));
    return bounds.length === 2 ? `${bounds[0]} – ${bounds[1]}` : String(bounds[0] ?? '');
  }
  if (Array.isArray(value)) {
    return value.map(entry => optionText(filter, entry)).join(', ');
  }
  return optionText(filter, value);
};
